// React のインポート（型定義用）
import React from 'react';

/**
 * 自動カタカナ変換ユーティリティ
 * IME入力時のひらがなをリアルタイムでカタカナに変換する機能を提供
 */

/**
 * ひらがなをカタカナに変換
 * @param str - 変換する文字列
 * @returns カタカナに変換された文字列
 */
export const hiraganaToKatakana = (str: string): string => {
  return str.replace(/[\u3041-\u3096]/g, (match) => {
    const chr = match.charCodeAt(0) + 0x60;
    return String.fromCharCode(chr);
  });
};

/**
 * 文字列がひらがなのみかどうかをチェック
 * @param str - チェックする文字列
 * @returns ひらがなのみの場合true、それ以外false
 */
export const isHiraganaOnly = (str: string): boolean => {
  // 空文字はfalse
  if (!str) return false;
  // ひらがな、スペース、改行のみで構成されているかチェック
  return /^[\u3040-\u309F\s]*$/.test(str);
};

/**
 * 自動カタカナ変換フック用のステート管理クラス
 */
export class AutoKanaHandler {
  private buffer: string = '';
  private isComposing: boolean = false;
  private previousValue: string = ''; // 変換開始前のフリガナ値を保存

  /**
   * CompositionStart イベントハンドラ
   * IME入力開始時に呼び出す
   * @param currentValue - 現在のフリガナ値（追記用）
   */
  handleCompositionStart = (currentValue: string = ''): void => {
    this.isComposing = true;
    this.buffer = '';
    this.previousValue = currentValue; // 現在の値を保存
  };

  /**
   * CompositionUpdate イベントハンドラ
   * IME入力中の変換候補が変わるたびに呼び出す
   * @param event - CompositionEvent
   * @returns 前の値 + 変換されたカタカナ文字列（追記）
   */
  handleCompositionUpdate = (event: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>): string => {
    if (!this.isComposing) {
      return '';
    }

    const data = event.data || '';
    // ひらがなのみをカタカナに変換
    const kana = hiraganaToKatakana(data);
    this.buffer = kana;
    // 前の値に追記
    return this.previousValue + kana;
  };

  /**
   * CompositionEnd イベントハンドラ
   * IME入力確定時に呼び出す
   * @param event - CompositionEvent
   * @returns 最終的に変換されたカタカナ文字列
   */
  handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>): string => {
    this.isComposing = false;
    const data = event.data || '';
    const kana = hiraganaToKatakana(data);
    this.buffer = kana;
    return kana;
  };

  /**
   * 入力イベントハンドラ
   * 通常の入力（コピペなど）にも対応
   * @param value - 入力値
   * @returns 変換されたカタカナ文字列
   */
  handleInput = (value: string): string => {
    if (this.isComposing) {
      return this.buffer;
    }
    // 通常入力の場合もひらがなをカタカナに変換
    return hiraganaToKatakana(value);
  };

  /**
   * バッファをクリア
   * @param keepPrevious - previousValueを保持するかどうか
   */
  reset = (keepPrevious: boolean = false): void => {
    this.buffer = '';
    this.isComposing = false;
    if (!keepPrevious) {
      this.previousValue = '';
    }
  };

  /**
   * 現在のバッファを取得
   */
  getBuffer = (): string => {
    return this.buffer;
  };
}
